/** Default CTA text for the embeddable OpenLink payment button. */
export const DEFAULT_BUTTON_TEXT = 'Make a payment';

/** Closinglock logo path, shared between the live preview and the generated embed snippet. */
export const LOGO_SVG_PATH =
    'M19.683 3.66 6.105 12.414v9.38a2.455 2.455 0 0 0 2.457 2.446h10.882a2.455 2.455 0 0 0 2.457-2.447v-9.38l-5.54-3.592 3.288-2.115 5.817 3.782v12.856c0 2.452-2.006 4.445-4.468 4.445H7.006c-2.462 0-4.463-1.993-4.463-4.445V10.496L16.392 1.52 14.059 0 0 9.104v14.491c.135 3.74 3.226 6.738 7.004 6.738h13.992c3.786 0 6.869-2.997 7.004-6.738V9.11l-8.315-5.45zm-.339 10.133v7.901H8.654v-7.901l5.36-3.458z';

/** Whether the string is a full-form hex color: 6-digit (`#0a1b2c`) or 8-digit with alpha (`#0a1b2cff`). */
export const isValidHex = (hex: string): boolean => /^#(?:[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(hex);

/** Expand a 3- or 4-digit shorthand hex (`#abc`, `#abcd`) to its full form (`#aabbcc`, `#aabbccdd`); pass through otherwise. */
export const expandHex = (hex: string): string => {
    const short = hex.match(/^#([0-9A-Fa-f]{3,4})$/);
    if (short) {
        return `#${[...short[1]].map(char => char + char).join('')}`;
    }
    return hex;
};

/** Normalize a user-entered color to a full-form hex, falling back to black. */
export const resolveButtonColor = (hex: string): string => {
    const expanded = expandHex(hex);
    return isValidHex(expanded) ? expanded : '#000000';
};

/** Pick black or white text for legibility against the given background hex (alpha, if present, is ignored). */
export const getContrastTextColor = (hex: string): string => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#000000' : '#ffffff';
};

/** Only http(s) URLs may appear in the embed snippet; anything else (e.g. `javascript:`) is dropped. */
const isSafeUrl = (url: string): boolean => {
    try {
        const { protocol } = new URL(url);
        return protocol === 'https:' || protocol === 'http:';
    } catch {
        return false;
    }
};

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';

interface AnchorHtmlParams {
    anchorText: string;
    url: string;
    backgroundColor: string;
    textColor: string;
}

/**
 * Build the standalone HTML snippet customers embed on their site.
 *
 * The markup is assembled through DOM APIs and serialized via `outerHTML` so the
 * browser's own escaping applies to every user-supplied value; never switch this
 * back to string concatenation.
 */
export const buildAnchorHtml = ({ anchorText, url, backgroundColor, textColor }: AnchorHtmlParams): string => {
    const anchor = document.createElement('a');
    if (isSafeUrl(url)) {
        anchor.href = url;
    }
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    anchor.style.cssText =
        'display: inline-flex; align-items: center; padding: 10px 24px; text-decoration: none; border-radius: 4px; font-family: Arial, sans-serif; font-size: 14px; font-weight: 600;';
    anchor.style.backgroundColor = backgroundColor;
    anchor.style.color = textColor;

    const svg = document.createElementNS(SVG_NAMESPACE, 'svg');
    svg.setAttribute('width', '14');
    svg.setAttribute('height', '16');
    svg.setAttribute('viewBox', '0 0 28 31');
    svg.setAttribute('fill', textColor);
    svg.style.cssText = 'display: inline-block; vertical-align: middle; margin-right: 6px; flex-shrink: 0;';

    const logoPath = document.createElementNS(SVG_NAMESPACE, 'path');
    logoPath.setAttribute('d', LOGO_SVG_PATH);
    svg.appendChild(logoPath);

    anchor.append(svg, document.createTextNode(anchorText || DEFAULT_BUTTON_TEXT));
    return anchor.outerHTML;
};
